import { useState } from 'react';
import { ChevronDown, ChevronUp, Lightbulb, Phone, Download } from 'lucide-react';

export const CalculatorCard = ({ 
  id,
  title, 
  subtitle, 
  icon: Icon, 
  children, 
  tip, 
  result, 
  showResult = false,
  testId,
  defaultOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div id={id} className="scroll-mt-20">
      {/* Collapsed State */}
      {!isOpen && (
        <div
          data-testid={`${testId}-collapsed`}
          onClick={() => setIsOpen(true)}
          className="calc-card-collapsed p-5 flex items-center justify-between cursor-pointer"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green/20 flex items-center justify-center">
              <Icon className="w-6 h-6 text-green" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg">{title}</h3>
              <p className="text-gray-400 text-sm">{subtitle}</p>
            </div>
          </div>
          <span className="text-green font-medium text-sm flex items-center gap-1">
            Otevřít kalkulačku
            <ChevronDown className="w-4 h-4" />
          </span>
        </div>
      )}

      {/* Expanded State */}
      {isOpen && (
        <div
          data-testid={testId}
          className="calc-card overflow-hidden"
        >
          {/* Header */}
          <div 
            className="p-5 flex items-center justify-between border-b border-white/10 cursor-pointer"
            onClick={() => setIsOpen(false)}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green/20 flex items-center justify-center">
                <Icon className="w-6 h-6 text-green" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-lg">{title}</h3>
                <p className="text-gray-400 text-sm">{subtitle}</p>
              </div>
            </div>
            <span className="text-gray-500 font-medium text-sm flex items-center gap-1">
              Skrýt kalkulačku
              <ChevronUp className="w-4 h-4" />
            </span>
          </div>

          {/* Calculator Content */}
          <div className="p-5 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left - Inputs */}
              <div className="space-y-6">
                {children}
              </div>

              {/* Right - Results */}
              {showResult && result && (
                <div className="space-y-4">
                  {result}
                </div>
              )}
            </div>

            {/* Tip Section */}
            {tip && (
              <div className="tip-box p-4 mt-6">
                <div className="flex gap-3">
                  <Lightbulb className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-gold font-semibold text-sm">Tip od Pana Čunga:</span>
                    <p className="text-gray-600 text-sm mt-1">{tip}</p>
                  </div>
                </div>
              </div>
            )}

            {/* CTA Section */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-white/10">
              <a
                href="https://pancungradi.simplybook.it/v2/#book"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
                data-testid={`${testId}-booking-btn`}
              >
                <button className="w-full bg-green hover:bg-green-dark text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" />
                  Probereme to osobně
                </button>
              </a>
              <a
                href="https://form.typeform.com/to/bA4J1ZKX?typeform-source=pancung.cz"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
                data-testid={`${testId}-plan-btn`}
              >
                <button className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" />
                  Chci finanční plán
                </button>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const SliderInput = ({ 
  label, 
  value, 
  onChange, 
  min, 
  max, 
  step = 1, 
  suffix = '', 
  formatValue,
  testId 
}) => {
  const displayValue = formatValue ? formatValue(value) : `${value.toLocaleString('cs-CZ')} ${suffix}`;
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-gray-400 text-sm font-medium">{label}</label>
      </div>
      <div className="white-slider">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={testId}
          style={{
            background: `linear-gradient(to right, #22C55E 0%, #22C55E ${percentage}%, rgba(255,255,255,0.1) ${percentage}%, rgba(255,255,255,0.1) 100%)`
          }}
        />
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-500 text-xs">{min.toLocaleString('cs-CZ')} {suffix}</span>
        <span className="text-white font-bold text-lg">{displayValue}</span>
        <span className="text-gray-500 text-xs">{max.toLocaleString('cs-CZ')} {suffix}</span>
      </div>
    </div>
  );
};

export const NumberInput = ({ 
  label, 
  value, 
  onChange, 
  suffix = '',
  placeholder,
  testId 
}) => {
  return (
    <div className="space-y-2">
      <label className="text-gray-400 text-sm font-medium block">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          placeholder={placeholder}
          data-testid={testId}
          className="number-input pr-12"
        />
        {suffix && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};

export const RiskSelector = ({ value, onChange, testId }) => {
  return (
    <div className="space-y-3">
      <label className="text-gray-400 text-sm font-medium block">Rizikový profil</label>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <button
            key={num}
            onClick={() => onChange(num)}
            data-testid={`${testId}-${num}`}
            className={`risk-btn ${value === num ? 'active' : ''}`}
          >
            {num}
          </button>
        ))}
      </div>
      <p className="text-gray-500 text-xs">
        Čím vyšší rizikový profil, tím více může výnos nebo ztráta vaší investice kolísat v čase.
      </p>
    </div>
  );
};
